import { Link } from "react-router-dom";
import { ExternalLink, Phone, MessageCircle } from "lucide-react";
import { SITE, WORKMANSHIP } from "@/config/site";
import { usePageTitle, Reveal, SectionHead, Eyebrow, RoadDash } from "@/components/bits";
import { QuoteCallout } from "./Home";

const HERO_IMG = "/work/equipment.jpg";

const STATS = [
  ["18", "Years in operation"],
  ["30+", "Years industry experience"],
  ["R/C/G", "Residential, commercial & government"],
];

export default function AboutPage() {
  usePageTitle("About");
  return (
    <>
      <section className="relative isolate overflow-hidden border-b border-white/10" data-testid="about-hero">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-joell-black/75 via-joell-black/60 to-joell-black" aria-hidden="true" />
        <div className="container-x relative pb-20 pt-36 md:pb-28 md:pt-48">
          <Reveal>
            <Eyebrow>About JOELL</Eyebrow>
            <h1 className="mt-4 max-w-5xl font-display text-4xl font-bold uppercase leading-[1.02] tracking-tight text-white sm:text-6xl">
              Built on experience. Driven by pride in the work.
            </h1>
          </Reveal>
        </div>
        <div className="absolute inset-x-0 bottom-0"><RoadDash /></div>
      </section>

      <section className="container-x py-20 md:py-28" data-testid="about-story-section">
        <div className="grid gap-14 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SectionHead
              title="Asphalt, construction and repair services — across Bermuda."
              className="!mb-8"
            />
            <Reveal>
              <div className="space-y-6 text-base leading-relaxed text-slate-300 sm:text-lg">
                <p>
                  Joell Asphalt Service &amp; Maintenance Ltd. is a Bermuda-based company led by{" "}
                  <span className="font-semibold text-white">Eston E Joell</span>, providing asphalt, construction
                  and repair services across the island.
                </p>
                <p>
                  The business is operated by <span className="font-semibold text-white">Eston E Joell</span>, who
                  brings more than 30 years of experience within the asphalt industry.
                </p>
                <p>
                  With years of hands-on experience, the company is built around practical knowledge, attention to
                  detail and pride in delivering work that is completed properly and finished to a high standard.
                </p>
                <p>
                  Joell Asphalt Service &amp; Maintenance Ltd. is a Bermuda-based owner-operated asphalt and
                  maintenance company specializing in residential, commercial and government projects. The company
                  has been operating for 18 years and provides asphalt paving, road maintenance, demolition,
                  construction, refurbishment, trucking and related maintenance services throughout Bermuda.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.08}>
              <div className="mt-10 grid grid-cols-3 gap-4" data-testid="about-stats">
                {STATS.map(([n, label]) => (
                  <div key={label} className="border border-white/10 bg-joell-surface p-4 sm:p-5">
                    <p className="font-display text-2xl font-bold text-joell-yellow sm:text-4xl">{n}</p>
                    <p className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.18em] text-slate-400">{label}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="space-y-8">
            <Reveal delay={0.1}>
              <div className="border border-white/10 bg-joell-surface p-6" data-testid="about-workmanship">
                <p className="eyebrow">The JOELL standard</p>
                <ul className="mt-4 space-y-2.5">
                  {WORKMANSHIP.map((w) => (
                    <li key={w} className="flex items-center gap-3 text-sm text-slate-300">
                      <span aria-hidden="true" className="inline-block h-2 w-2 shrink-0 rotate-45 border-t-2 border-r-2 border-joell-green" />
                      {w}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.16}>
              <Link to="/request-quote" className="btn-accent w-full" data-testid="about-quote-button">Request a Free Quote</Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-joell-coal/50 py-20 md:py-28" data-testid="workforce-section">
        <div className="container-x grid items-center gap-12 lg:grid-cols-2">
          <div>
            <SectionHead
              eyebrow="Community & training"
              title="Workforce development & training"
              sub="Joell Asphalt Service & Maintenance Ltd. is committed to supporting the development of Bermuda's workforce by providing practical training opportunities and industry exposure for Bermudians interested in construction and asphalt services."
              className="!mb-8"
            />
            <Reveal delay={0.1}>
              <a
                href="https://bernews.com/2023/08/heavy-highway-construction-asphalt-training/"
                target="_blank"
                rel="noreferrer"
                className="btn-outline"
                data-testid="workforce-article-link"
              >
                Bermudians complete highway construction training course <ExternalLink size={14} />
              </a>
            </Reveal>
          </div>
          <Reveal delay={0.12}>
            <div className="relative">
              <div className="absolute -right-3 -top-3 h-full w-full border-2 border-joell-yellow" aria-hidden="true" />
              <img
                src="/work/equipment.jpg"
                alt="JOELL equipment and machinery on site"
                loading="lazy"
                className="relative w-full border border-white/10 object-cover aspect-[4/3]"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="container-x py-16 md:py-20">
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-6 border border-white/10 bg-joell-surface p-8 sm:p-10 md:flex-row md:items-center" data-testid="about-contact-cta">
            <div>
              <h2 className="font-display text-2xl font-bold uppercase text-white sm:text-3xl">Talk to JOELL about your project.</h2>
              <p className="mt-2 text-slate-300">Straightforward advice and a simple quote process.</p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <a href={SITE.phoneHref} className="btn-accent" data-testid="about-call-button"><Phone size={15} /> Call {SITE.phone}</a>
              <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="btn-outline" data-testid="about-whatsapp-button">
                <MessageCircle size={15} /> WhatsApp JOELL
              </a>
            </div>
          </div>
        </Reveal>
      </section>

      <QuoteCallout />
    </>
  );
}
